/**
 * Regras de exibição do Fechamento Contábil Mensal — sem React, para ter teste.
 *
 * O servidor já entrega tudo calculado (parecer, etapas, contagens). Aqui fica só o
 * que é da tela: rótulos, cores com significado, formatação e o agrupamento das
 * constatações por tipo.
 */
import type {
  EstadoEtapa,
  Etapa,
  IVerificacao,
  Parecer,
  Severidade,
} from '../../../../hooks/useFechamentoContabilMensal';

export const BASE_ROTA = '/modulesHome/accountingConciliation/fechamento-contabil-mensal';

// Verde, vermelho e âmbar carregam significado (ok, erro, atenção), como no resto
// do admin; azul é "em andamento" e cinza é "não se aplica".
export const COR = {
  ok: '#16A34A',
  okSuave: '#E5F5ED',
  erro: '#DC2626',
  erroSuave: '#FBE9E7',
  atencao: '#D97706',
  atencaoSuave: '#FCF1DD',
  andamento: '#1B88F5',
  andamentoSuave: '#E9F4FD',
  neutro: '#94A3B8',
  neutroSuave: '#EEF2F6',
  tinta: '#0F172A',
  apagado: '#71717A',
  borda: '#E4E4E7',
} as const;

interface IAparencia {
  rotulo: string;
  cor: string;
  suave: string;
  explicacao: string;
}

export const PARECER: Record<Parecer, IAparencia> = {
  bloquear: {
    rotulo: 'Bloquear',
    cor: COR.erro,
    suave: COR.erroSuave,
    explicacao: 'Não liberar ao cliente até resolver as constatações críticas.',
  },
  em_aberto: {
    rotulo: 'Em aberto',
    cor: COR.andamento,
    suave: COR.andamentoSuave,
    explicacao:
      'A contabilidade desta competência ainda não foi concluída no Domínio. A revisão é preliminar.',
  },
  liberar_com_ressalvas: {
    rotulo: 'Liberar com ressalvas',
    cor: COR.atencao,
    suave: COR.atencaoSuave,
    explicacao: 'Pode ser liberado com os ajustes e ressalvas indicados.',
  },
  liberar: {
    rotulo: 'Liberar',
    cor: COR.ok,
    suave: COR.okSuave,
    explicacao: 'Sem constatações críticas ou altas em aberto.',
  },
  sem_movimento: {
    rotulo: 'Sem movimento',
    cor: COR.neutro,
    suave: COR.neutroSuave,
    explicacao:
      'A competência não teve operação. As verificações que pressupõem movimento ficam suspensas.',
  },
};

export const aparenciaDoParecer = (p: string): IAparencia =>
  PARECER[p as Parecer] ?? { rotulo: p, cor: COR.neutro, suave: COR.neutroSuave, explicacao: '' };

export const ORDEM_SEVERIDADE: Severidade[] = ['critica', 'alta', 'media', 'baixa', 'informativa'];

export const SEVERIDADE: Record<
  Severidade,
  { rotulo: string; cor: string; suave: string; leitura: string }
> = {
  critica: {
    rotulo: 'Crítica',
    cor: COR.erro,
    suave: COR.erroSuave,
    leitura:
      'Compromete os relatórios ou expõe o cliente a risco fiscal grave. Bloqueia a liberação.',
  },
  alta: {
    rotulo: 'Alta',
    cor: COR.atencao,
    suave: COR.atencaoSuave,
    leitura:
      'Distorção relevante em conta, resultado ou indicador. Exige ajuste ou aceite do responsável técnico.',
  },
  media: {
    rotulo: 'Média',
    cor: '#A16207',
    suave: '#FEF9C3',
    leitura:
      'Distorção pequena ou erro de classificação. Corrigir nesta competência ou na próxima.',
  },
  baixa: {
    rotulo: 'Baixa',
    cor: COR.andamento,
    suave: COR.andamentoSuave,
    leitura: 'Melhoria de processo ou organização.',
  },
  informativa: {
    rotulo: 'Informativa',
    cor: COR.apagado,
    suave: COR.neutroSuave,
    leitura: 'Tendência, oportunidade ou preparação normativa.',
  },
};

// `curto` é o rótulo da trilha no card, onde cabem só quatro palavras curtas
export const ETAPAS: { chave: Etapa; nome: string; curto: string; descricao: string }[] = [
  {
    chave: 'fiscal',
    nome: 'Integração fiscal',
    curto: 'fiscal',
    descricao: 'notas e tributos do fiscal com lançamento contábil',
  },
  { chave: 'folha', nome: 'Folha', curto: 'folha', descricao: 'folha do mês contabilizada' },
  {
    chave: 'banco',
    nome: 'Conciliação',
    curto: 'banco',
    descricao: 'extrato importado e conciliado',
  },
  {
    chave: 'consolidacao',
    nome: 'Consolidação',
    curto: 'fecham.',
    descricao: 'balancete fecha e a competência libera',
  },
];

export const corDaEtapa = (estado: EstadoEtapa): { cor: string; suave: string; rotulo: string } => {
  if (estado === 'ok') return { cor: COR.ok, suave: COR.okSuave, rotulo: 'concluída' };
  if (estado === 'pendente') return { cor: COR.erro, suave: COR.erroSuave, rotulo: 'parada' };
  if (estado === 'parcial')
    return { cor: COR.atencao, suave: COR.atencaoSuave, rotulo: 'em andamento' };
  return { cor: COR.neutro, suave: COR.neutroSuave, rotulo: 'não se aplica' };
};

export const FILA: {
  chave: 'bloquear' | 'em_aberto' | 'liberar';
  titulo: string;
  verbo: string;
  parecer: Parecer;
}[] = [
  { chave: 'bloquear', titulo: 'Travadas', verbo: 'não podem ir ao cliente', parecer: 'bloquear' },
  {
    chave: 'em_aberto',
    titulo: 'Verificar divergências',
    verbo: 'competência ainda aberta',
    parecer: 'em_aberto',
  },
  {
    chave: 'liberar',
    titulo: 'Liberadas para importação',
    verbo: 'com ou sem ressalvas, ou sem movimento',
    parecer: 'liberar',
  },
];

export const MODULO: Record<string, string> = {
  conciliacao: 'Conciliação bancária',
  cruzamentos: 'Cruzamentos',
  constatacoes: 'Constatações',
  demonstracoes: 'Demonstrações',
};

export const REGIME: Record<string, string> = {
  simples: 'Simples Nacional',
  presumido: 'Lucro Presumido',
  real: 'Lucro Real',
};

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026-08" -> "ago" */
export const mesCurto = (competencia: string): string =>
  MESES[Number(competencia.slice(5, 7)) - 1] ?? '';

/** "2026-08" -> "ago/2026" */
export const mesLongo = (competencia: string): string =>
  `${mesCurto(competencia)}/${competencia.slice(0, 4)}`;

/** Todos os meses de `ini` a `fim` (AAAA-MM), inclusive. */
export const mesesEntre = (ini: string, fim: string): string[] => {
  const saida: string[] = [];
  let ano = Number(ini.slice(0, 4));
  let mes = Number(ini.slice(5, 7));
  while (`${ano}-${String(mes).padStart(2, '0')}` <= fim && saida.length < 60) {
    saida.push(`${ano}-${String(mes).padStart(2, '0')}`);
    mes += 1;
    if (mes > 12) {
      mes = 1;
      ano += 1;
    }
  }
  return saida;
};

/** Primeira competência oferecida nos seletores: a carga do Domínio tem dado a partir de 2024. */
export const PRIMEIRO_MES = '2024-01';

/** Exercício corrente até o mês de `hoje`: é o período em que a tela abre. */
/**
 * Do início do exercício até o mês anterior: o mês corrente não é revisado, porque o
 * balancete dele ainda está sendo lançado (decisão de 16/10/2026). Em janeiro, o
 * exercício anterior inteiro.
 */
export const periodoPadrao = (hoje: Date): { ini: string; fim: string } => {
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  const ano = anterior.getFullYear();
  return { ini: `${ano}-01`, fim: `${ano}-${String(anterior.getMonth() + 1).padStart(2, '0')}` };
};

/**
 * "Centavos / Arredondado" (origem, item 7). Só a exibição arredonda: os totais
 * continuam somados com centavos, então um total pode diferir em ±1 da soma das
 * linhas mostradas. A preferência fica no navegador de quem usa.
 */
const CHAVE_DO_ARREDONDADO = 'fechamento-contabil-mensal:arredondado';

const lerArredondado = (): boolean => {
  try {
    return window.localStorage.getItem(CHAVE_DO_ARREDONDADO) === '1';
  } catch {
    return false;
  }
};

let arredondado = lerArredondado();

export const estaArredondado = (): boolean => arredondado;

export const definirArredondado = (valor: boolean): void => {
  arredondado = valor;
  try {
    window.localStorage.setItem(CHAVE_DO_ARREDONDADO, valor ? '1' : '0');
  } catch {
    // sem armazenamento no navegador: a escolha vale só nesta visita
  }
};

export const brl = (valor: number | null | undefined): string =>
  valor == null
    ? '—'
    : valor.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        ...(arredondado ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
      });

/** Valor curto para card: R$ 1,2 mi / R$ 340 mil / R$ 980. */
export const brlCurto = (valor: number | null | undefined): string => {
  if (valor == null) return '—';
  const abs = Math.abs(valor);
  const sinal = valor < 0 ? '-' : '';
  const um = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  if (abs >= 1e6) return `${sinal}R$ ${um(abs / 1e6)} mi`;
  if (abs >= 1e3) return `${sinal}R$ ${um(abs / 1e3)} mil`;
  return `${sinal}R$ ${abs.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

export const pct = (valor: number | null | undefined, casas = 1): string =>
  valor == null
    ? '—'
    : `${(valor * 100).toLocaleString('pt-BR', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas,
      })}%`;

/** Variação relativa de `atual` contra `anterior`; null quando não dá para comparar. */
export const variacao = (
  atual: number | null | undefined,
  anterior: number | null | undefined,
): number | null => {
  if (atual == null || anterior == null || !anterior) return null;
  return (atual - anterior) / Math.abs(anterior);
};

export interface IGrupoDeConstatacoes {
  chave: string; // "E5.3 · PIS apurado e não contabilizado"
  pior: Severidade;
  itens: IVerificacao[];
  total: number;
}

/**
 * Uma linha por tipo de constatação (código + título), com a pior gravidade, a
 * quantidade e o valor somado. Ordem: gravidade, depois quantidade.
 */
export const agruparConstatacoes = (itens: IVerificacao[]): IGrupoDeConstatacoes[] => {
  const grupos = new Map<string, IVerificacao[]>();
  itens.forEach(item => {
    const chave = `${item.codigo} · ${item.titulo}`;
    grupos.set(chave, [...(grupos.get(chave) ?? []), item]);
  });
  return Array.from(grupos.entries())
    .map(([chave, doGrupo]) => ({
      chave,
      itens: doGrupo,
      pior:
        ORDEM_SEVERIDADE.find(s => doGrupo.some(x => x.severidade === s)) ??
        ('informativa' as const),
      total: doGrupo.reduce((soma, x) => soma + (x.valor ?? 0), 0),
    }))
    .sort(
      (a, b) =>
        ORDEM_SEVERIDADE.indexOf(a.pior) - ORDEM_SEVERIDADE.indexOf(b.pior) ||
        b.itens.length - a.itens.length,
    );
};

export interface ITipoNoPeriodo {
  chave: string;
  pior: Severidade;
  itens: number;
  /** meses em que o tipo aparece, em ordem */
  meses: string[];
  total: number;
}

/**
 * Quadro "No período" das constatações (origem, item 3): uma linha por tipo, com os
 * meses em que aparece. Mesma ordem do resumo por tipo: gravidade, depois quantidade.
 */
export const tiposNoPeriodo = (
  itens: {
    competencia: string;
    codigo: string;
    titulo: string;
    severidade: Severidade;
    valor: number | null;
  }[],
): ITipoNoPeriodo[] => {
  const grupos = new Map<string, typeof itens>();
  itens.forEach(item => {
    const chave = `${item.codigo} · ${item.titulo}`;
    grupos.set(chave, [...(grupos.get(chave) ?? []), item]);
  });
  return Array.from(grupos.entries())
    .map(([chave, doTipo]) => ({
      chave,
      itens: doTipo.length,
      pior:
        ORDEM_SEVERIDADE.find(s => doTipo.some(x => x.severidade === s)) ??
        ('informativa' as const),
      meses: Array.from(new Set(doTipo.map(x => x.competencia))).sort(),
      total: doTipo.reduce((soma, x) => soma + (x.valor ?? 0), 0),
    }))
    .sort(
      (a, b) =>
        ORDEM_SEVERIDADE.indexOf(a.pior) - ORDEM_SEVERIDADE.indexOf(b.pior) || b.itens - a.itens,
    );
};

/** Filtro da tela de constatações: gravidades marcadas e texto livre. */
export const filtrarConstatacoes = (
  itens: IVerificacao[],
  severidades: Severidade[],
  busca: string,
): IVerificacao[] => {
  const termo = busca.trim().toLowerCase();
  return itens.filter(
    item =>
      severidades.includes(item.severidade) &&
      (!termo || JSON.stringify(item).toLowerCase().includes(termo)),
  );
};

/** Como o resto do sistema escreve uma conta: classificação · código · nome. */
export const textoDaConta = (
  codigo: string,
  plano: Record<string, { classificacao: string; nome: string }> | undefined,
): string => {
  const conta = plano?.[codigo];
  return conta ? `${conta.classificacao} · ${codigo} · ${conta.nome}` : codigo;
};
